import React, { Suspense, lazy } from "react"
import { Switch, Route, useLocation } from "react-router-dom"
import {
  ThemeProvider,
  StyledEngineProvider,
  createTheme,
} from "@mui/material/styles"
import { Helmet } from "react-helmet"
import CssBaseline from "@mui/material/CssBaseline"

import { page } from "../utils/analytics"
import "../index.css"
import ErrorBoundary from "../components/ErrorBoundary"
import Loading from "../components/template-parts/Loading"

const AsyncAppRouter = lazy(() => import("./AppRouter"))

declare module "@mui/material/styles" {
  interface Palette {
    accentOne: Palette["primary"]
    accentTwo: Palette["primary"]
    accentThree: Palette["primary"]
  }
  interface PaletteOptions {
    accentOne: PaletteOptions["primary"]
    accentTwo: PaletteOptions["primary"]
    accentThree: PaletteOptions["primary"]
  }
}

const theme = createTheme({
  palette: {
    primary: {
      main: "#1E2631", // dark navy
    },
    secondary: {
      main: "#0BD1C2", // teal
    },
    accentOne: {
      main: "#FF70A6", // pink
      dark: "#E00056",
    },
    accentTwo: {
      main: "#9EF62E", // lime green
    },
    accentThree: {
      main: "#77AEAB", // "storm" (grey-ish)
    },
    text: {
      primary: "#222222",
    },
  },
  typography: {
    fontFamily: "Poppins, sans-serif",
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          "&:hover": {
            backgroundColor: "#0BD1C2",
            color: "#1E2631",
          },
        },
      },
    },
  },
})

const ScrollToTop = () => {
  window.scrollTo(0, 0)
  return null
}

const App = () => {
  const [prevPath, setPrevPath] = React.useState("")
  const location = useLocation()

  React.useEffect(() => {
    const nextPage = `${location.pathname}${location.search}`
    if (prevPath !== nextPage) {
      page(nextPage)
      setPrevPath(nextPage)
    }
  }, [location.pathname, location.search, prevPath])

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <ErrorBoundary>
          <>
            <Helmet titleTemplate="%s | Driving Lessons & Drivers Ed by Coastline">
              <title>The Modern Driving School</title>
              <meta
                name="description"
                content="Coastline Academy is the modern driving school. Book a driving lesson in your city today."
              />
            </Helmet>

            <Suspense fallback={<Loading />}>
              <Route>
                <ScrollToTop />
              </Route>
              <Switch>
                <Route path="*">
                  <AsyncAppRouter />
                </Route>
              </Switch>
            </Suspense>
          </>
        </ErrorBoundary>
      </ThemeProvider>
    </StyledEngineProvider>
  )
}

export default App
